import { useCallback, useEffect, useRef, useState } from 'react';
import { useShowAlert } from 'hooks';
import { SECONDARY_API_CLIENT } from 'services/API';
import strings from 'strings';
import { DateFormats } from 'UI/constants/defaults';
import { UIStatus } from 'UI/constants/status';
import { localTimeFormatter } from 'UI/utils';

const DEFAULT_SAVE_OPTIONS = {
  silent: false
};

const normalizeSaveOptions = (options = {}) => ({
  silent: options.silent === true
});

const mergeSaveOptions = (currentOptions, nextOptions = {}) => {
  const normalizedNextOptions = normalizeSaveOptions(nextOptions);

  if (!currentOptions) {
    return normalizedNextOptions;
  }

  return {
    silent: currentOptions.silent && normalizedNextOptions.silent
  };
};

const buildFormSnapshot = draftData => {
  const { draftId: _draftId, draftUpdatedAt: _draftUpdatedAt, ...formSnapshot } = draftData || {};

  return formSnapshot;
};

export const useDrafts = ({ buildBody, initialDraft, entity, endpoint, onSaved }) => {
  const { showAlert } = useShowAlert();
  const isConfigured = Boolean(buildBody && entity && endpoint);
  const [draft, setDraft] = useState(initialDraft);
  const [uiDraftState, setUiDraftState] = useState({
    status: UIStatus.Default
  });
  const draftRef = useRef(initialDraft);
  const lastSavedSnapshotRef = useRef(null);
  const isSavingDraftRef = useRef(false);
  const isMountedRef = useRef(true);
  const pendingOptionsRef = useRef(null);
  const pendingUpdatesRef = useRef(null);
  const persistenceRevisionRef = useRef(0);
  const saveCyclePromiseRef = useRef(null);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);

  const draftUpdatedAt = draft?.draftUpdatedAt
    ? localTimeFormatter(draft.draftUpdatedAt, DateFormats.SimpleDateTime)
    : null;

  const updateDraft = useCallback(draftUpdates => {
    if (!isMountedRef.current) return;

    setDraft(prev => {
      const nextDraft = { ...prev, ...draftUpdates };
      draftRef.current = nextDraft;

      return nextDraft;
    });
  }, []);

  const detachDraft = useCallback(draftId => {
    if (!isMountedRef.current || draftRef.current?.draftId !== draftId) return;

    persistenceRevisionRef.current += 1;
    const detachedDraft = {
      ...draftRef.current,
      draftId: null,
      draftUpdatedAt: null
    };
    draftRef.current = detachedDraft;
    setDraft(detachedDraft);
  }, []);

  const persistPendingSaves = useCallback(
    async persistedDraftId => {
      if (!pendingUpdatesRef.current) {
        return persistedDraftId;
      }

      const snapshotOptions = pendingOptionsRef.current || DEFAULT_SAVE_OPTIONS;
      const snapshotUpdates = pendingUpdatesRef.current;
      const currentDraft = draftRef.current;
      const currentDraftId = currentDraft?.draftId;
      const persistenceRevision = persistenceRevisionRef.current;
      const draftData = {
        ...currentDraft,
        ...snapshotUpdates
      };

      pendingUpdatesRef.current = null;
      pendingOptionsRef.current = null;

      if (isMountedRef.current) {
        setUiDraftState(prev => ({ ...prev, status: UIStatus.Saving }));
      }

      const body = buildBody(buildFormSnapshot(draftData), entity);
      const response = await SECONDARY_API_CLIENT[currentDraftId ? 'patch' : 'post'](
        currentDraftId ? `${endpoint}/${currentDraftId}` : endpoint,
        body
      ).catch(error => {
        // If the draft was detached while this request was in flight (e.g. it
        // was deleted), the failure targets a draft that no longer exists.
        // Re-queue the in-flight edits and retry against the fresh draftId
        // instead of losing them.
        if (persistenceRevision === persistenceRevisionRef.current) {
          throw error;
        }
        pendingUpdatesRef.current = {
          ...snapshotUpdates,
          ...pendingUpdatesRef.current
        };
        pendingOptionsRef.current = mergeSaveOptions(snapshotOptions, pendingOptionsRef.current);

        return null;
      });

      if (response === null || persistenceRevision !== persistenceRevisionRef.current) {
        if (!pendingUpdatesRef.current && isMountedRef.current) {
          setUiDraftState(prev => ({ ...prev, status: UIStatus.Default }));
        }
        return persistPendingSaves(draftRef.current?.draftId || null);
      }

      const nextPersistedDraftId = currentDraftId || response.data.id;

      updateDraft({
        draftId: nextPersistedDraftId,
        draftUpdatedAt: new Date().toISOString()
      });
      onSaved && onSaved(nextPersistedDraftId);

      lastSavedSnapshotRef.current = buildFormSnapshot(draftData);

      if (!snapshotOptions.silent) {
        showAlert({
          title: strings.drafts.alert.success.save.title
        });
      }
      if (isMountedRef.current) {
        setUiDraftState(prev => ({ ...prev, status: UIStatus.Success }));
      }

      return persistPendingSaves(nextPersistedDraftId);
    },
    [buildBody, entity, endpoint, onSaved, showAlert, updateDraft]
  );

  const saveDraft = useCallback(
    async (updates = {}, options = {}) => {
      if (!isConfigured) {
        return Promise.resolve(null);
      }

      draftRef.current = {
        ...draftRef.current,
        ...updates
      };
      pendingUpdatesRef.current = pendingUpdatesRef.current
        ? {
            ...pendingUpdatesRef.current,
            ...updates
          }
        : { ...updates };
      pendingOptionsRef.current = mergeSaveOptions(pendingOptionsRef.current, options);

      if (isSavingDraftRef.current) {
        return saveCyclePromiseRef.current;
      }

      isSavingDraftRef.current = true;
      saveCyclePromiseRef.current = persistPendingSaves(draftRef.current?.draftId || null)
        .catch(error => {
          if (isMountedRef.current) {
            setUiDraftState(prev => ({ ...prev, status: UIStatus.Error }));
          }
          showAlert({
            title: strings.shared.ui.error,
            error
          });
          return null;
        })
        .finally(() => {
          isSavingDraftRef.current = false;
          pendingOptionsRef.current = null;
          pendingUpdatesRef.current = null;
          saveCyclePromiseRef.current = null;
        });

      return saveCyclePromiseRef.current;
    },
    [isConfigured, persistPendingSaves, showAlert]
  );

  const deleteDraftById = useCallback(
    async draftId => {
      if (!isConfigured) {
        return;
      }

      try {
        await SECONDARY_API_CLIENT.delete(`${endpoint}/${draftId}`);
      } catch (error) {
        if (!isMountedRef.current) return;

        showAlert({
          title: strings.shared.ui.error,
          error
        });
      }
    },
    [endpoint, isConfigured, showAlert]
  );

  const getPendingSavePromise = useCallback(() => saveCyclePromiseRef.current, []);

  return {
    deleteDraftById,
    detachDraft,
    draftId: draft?.draftId,
    draftUpdatedAt,
    getPendingSavePromise,
    isSavingDraft: uiDraftState.status === UIStatus.Saving,
    lastSavedSnapshotRef,
    saveDraft,
    updateDraft
  };
};
